// AI解析タブのデータ取得
import { ref, watch } from 'vue';

export const useAiAnalysis = ({ selectedItem, detailPlayerRef }) => {
  const aiAnalysisData = ref(null);
  const aiAnalysisLoading = ref(false);
  const aiAnalysisError = ref(null);

  const cache = {};
  const inflight = {};

  // 取得した応答を「今選んでいるファイルのもの」だけ画面へ入れるための照合。
  //
  // なぜ必要か (docs/emvd_merged/120-bug-ai-analysis-stale-data.md):
  //   (1) resources/js/exa/dashboard/useSelection.js:173-186
  //       restoreSelection が localStorage の exa_dashboard_item_id から前の選択を復元する
  //   (2) resources/js/exa/app.js:1049
  //       onMounted で restoreSelection を呼ぶため、開き直すと前のファイル (資料) が選ばれる
  //   (3) 本ファイル下部の watch(selectedItem) (:103-112)
  //       復元された資料の fetchAiAnalysis が始まる
  //   (4) その応答が返る前に別のファイル (画像) をクリックすると 2 本目の取得が始まる
  //   (5) 本ファイルの aiAnalysisData への代入 (:83) で videoId と今の選択を
  //       照合していなかったため、
  //       後から返った方が勝ち、画像を選んでいるのに資料の応答が入っていた
  //   (6) resources/views/exa/dashboard/emvd-ai/partials/_page-details.blade.php:3
  //       v-if="aiAnalysisData.page_details.length" が資料のデータで成立し続け、
  //       画像の AI 解析タブにページ別の解析が残って見えた
  //
  // 選択が無い (null) ときは取り違える相手がいないので、そのまま画面へ入れる。
  // id は localStorage 経由で文字列になる場合があるため String() で比較する
  // (useDetailPaneJump.js:154 の照合と同じ形)。
  const isCurrentItem = (videoId) => {
    const current = selectedItem.value;
    return !current || String(current.id) === String(videoId);
  };

  const fetchAiAnalysis = (videoId) => {
    if (cache[videoId]) {
      // キャッシュ経路も同じ照合が要る。別ファイルの取得中に戻ってきたときは
      // 取り残された aiAnalysisLoading / aiAnalysisError もここで畳む
      if (isCurrentItem(videoId)) {
        aiAnalysisData.value = cache[videoId];
        aiAnalysisError.value = null;
        aiAnalysisLoading.value = false;
      }
      return Promise.resolve();
    }
    // 取得中に別経路 (ジャンプ処理) から呼ばれても 1 本にまとめる
    if (inflight[videoId]) {
      return inflight[videoId];
    }

    if (isCurrentItem(videoId)) {
      aiAnalysisLoading.value = true;
      aiAnalysisError.value = null;
      aiAnalysisData.value = null;
    }

    const request = (async () => {
      try {
        const response = await fetch(`/drive/emvd-ai/analysis/detail/${videoId}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (response.status === 404) {
          if (isCurrentItem(videoId)) {
            aiAnalysisError.value = '解析データが見つかりません';
          }
          return;
        }

        if (!response.ok) {
          if (isCurrentItem(videoId)) {
            aiAnalysisError.value = '通信エラーが発生しました';
          }
          return;
        }

        const data = await response.json();
        // cache への保存は選択に関係なく行う (次に選んだときに使える)。
        // 画面への代入だけを「今の選択と一致するとき」に限る
        cache[videoId] = data;
        if (isCurrentItem(videoId)) {
          aiAnalysisData.value = data;
        }
      } catch (error) {
        if (isCurrentItem(videoId)) {
          aiAnalysisError.value = '通信エラーが発生しました';
        }
      } finally {
        // 別ファイルの取得が終わっても、今のファイルのローディングは消さない
        if (isCurrentItem(videoId)) {
          aiAnalysisLoading.value = false;
        }
        delete inflight[videoId];
      }
    })();

    inflight[videoId] = request;

    return request;
  };

  watch(selectedItem, (nextItem) => {
    if (nextItem && nextItem.ai_analysis_status === 'analyzed') {
      fetchAiAnalysis(nextItem.id);
      return;
    }
    // 解析済みでないファイル / 未選択に変わったときは、前のファイルの表示を残さない
    aiAnalysisData.value = null;
    aiAnalysisError.value = null;
    aiAnalysisLoading.value = false;
  });

  const formatSeconds = (sec) => {
    if (sec === null || sec === undefined) {
      return '';
    }
    const total = Math.trunc(Number(sec));
    if (!Number.isFinite(total)) {
      return '';
    }
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const sceneThumbnailPreviewUrl = ref(null);

  const openSceneThumbnailPreview = (url) => {
    if (!url) {
      return;
    }
    sceneThumbnailPreviewUrl.value = url;
    window.jQuery('#sceneThumbnailModal').modal('show');
  };

  const seekableKinds = ['video', 'audio'];

  const seekToTranscriptSegment = (segment) => {
    const item = selectedItem.value;
    const start = Number(segment?.start);

    if (
      !item ||
      !seekableKinds.includes(item.kind) ||
      !item.can_preview ||
      !item.manifest_url ||
      !Number.isFinite(start) ||
      start < 0
    ) {
      return;
    }

    detailPlayerRef.value?.seekTo(start);
  };

  return {
    aiAnalysisData,
    aiAnalysisLoading,
    aiAnalysisError,
    fetchAiAnalysis,
    formatSeconds,
    sceneThumbnailPreviewUrl,
    openSceneThumbnailPreview,
    seekToTranscriptSegment,
  };
};
